import { CommonModule } from '@angular/common';
import { Component, DestroyRef, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ProjectService } from 'src/app/services/projects.service';
import type { FileSourceComponent, ImportDoor, SourceFileDto, SourceRef } from 'src/app/shared/file-source/file-source';
import { ForestCloudService } from './forest-cloud.service';
import { environment } from 'src/environments/environment';
import { Env, EyeForestModules, EyeForestModulesUrls } from 'src/app/constants/eyeforest-modules';
import { forestCloudUrl } from 'src/app/utils/eyeforest-modules.utils';

/**
 * The ForestCloud archive of the project in focus, as a source for the
 * Import Wizard. Lists what was received (name, format, size, sender, date,
 * CRS), says why a file cannot be imported, and hands the chosen reference to
 * the picker. Which door (geo/tabular) the person is at decides what is
 * pickable here; the backend decides again on the bytes.
 */
@Component({
  selector: 'app-forest-cloud-source',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './forest-cloud-source.component.html',
  styleUrl: './forest-cloud-source.component.scss',
  host: { class: 'tw:block tw:w-full' },
})
export class ForestCloudSourceComponent implements OnInit, OnChanges, FileSourceComponent {
  @Input() accept: ImportDoor = 'geo';
  @Input() isLoading = false;
  @Input() repositoryId?: string;
  @Output() picked = new EventEmitter<SourceRef>();

  readonly SOURCE = 'forestcloud';
  readonly items = signal<SourceFileDto[]>([]);
  readonly total = signal(0);
  readonly page = signal(1);
  /** 10 rows fit the repository panel without a scroll inside a scroll. */
  readonly perPage = 10;
  readonly q = signal('');
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly reason = signal<string | null>(null);
  /** set on the click, cleared when the parent's isLoading falls: one wizard per pick */
  readonly picking = signal(false);

  private projectId = '';
  private readonly destroyRef = inject(DestroyRef);

  constructor(private readonly service: ForestCloudService, private readonly projects: ProjectService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['isLoading'] && !this.isLoading) this.picking.set(false);
    if (changes['repositoryId'] && !changes['repositoryId'].firstChange && this.projectId) this.load();
  }

  ngOnInit(): void {
    this.projects.projectId$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((id) => {
      if (!id || id === this.projectId) return;
      this.projectId = id;
      this.page.set(1);
      this.load();
    });
  }

  load(): void {
    if (!this.projectId) return;
    this.loading.set(true);
    this.error.set(null);
    this.service.listFiles(this.SOURCE, this.projectId, { q: this.q() || undefined, kind: this.accept, repository_id: this.repositoryId || undefined, page: this.page(), per_page: this.perPage }).subscribe({
      next: (p) => {
        this.items.set(p.items);
        this.total.set(p.total);
        this.reason.set(p.reason);
        this.loading.set(false);
      },
      error: (e) => {
        this.items.set([]);
        this.error.set(e?.error?.detail || 'Não foi possível ler o acervo do ForestCloud.');
        this.loading.set(false);
      },
    });
  }

  search(value: string): void {
    this.q.set(value.trim());
    this.page.set(1);
    this.load();
  }

  goTo(page: number): void {
    if (page < 1 || page > this.pages()) return;
    this.page.set(page);
    this.load();
  }

  pages(): number {
    return Math.max(1, Math.ceil(this.total() / this.perPage));
  }

  /** Pickable here: importable and of this door's kind. */
  canPick(f: SourceFileDto): boolean {
    return f.importable && f.kind === this.accept;
  }

  /** Why not, in the person's words. */
  why(f: SourceFileDto): string | null {
    if (f.importable && f.kind !== this.accept) return f.kind === 'geo' ? 'arquivo geográfico: use a aba de camadas espaciais' : 'arquivo tabular: use a aba de tabelas';
    return f.reason;
  }

  /** The ForestCloud archive of this project, in this environment (cross-app navigation). */
  openInForestCloudUrl(): string {
    return forestCloudUrl(EyeForestModulesUrls[EyeForestModules.ForestCloud][environment.eyeforestModulesDomain as Env], this.projectId);
  }

  /** true while a pick is being turned into a wizard: the buttons wait. */
  busy(): boolean {
    return this.isLoading || this.picking();
  }

  pick(f: SourceFileDto): void {
    if (!this.canPick(f) || this.busy()) return;
    this.picking.set(true);
    this.picked.emit({ source_type: this.SOURCE, ref: f.ref, name: f.name, size_bytes: f.size_bytes, kind: f.kind });
  }

  /** "importado em 24/09/2026 13:12 por Fulana" here, or "em outro repositório" (backend scope). */
  importedText(f: SourceFileDto): string | null {
    const i = f.imported;
    if (!i) return null;
    const when = i.at ? new Date(i.at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
    const by = i.by ? ` por ${i.by}` : '';
    return i.same_repository ? `importado${when ? ` em ${when}` : ''}${by}` : `importado em outro repositório${when ? ` em ${when}` : ''}${by}`;
  }

  size(bytes: number | null): string {
    if (bytes == null) return '—';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }
}
